import {
	CompanyTenantSearch,
	IEjariApi,
	LeasedPropertySearch,
	LookupTransformer,
	PersonTenantSearchRequest,
	TenantPropertySearch,
	TenantReqDetailsRequest, VerifyOTP,
	VerifyTentUserProfile
} from "./IApi";
import cfg from "./config";
import { base64ToArrayBuffer, httpGet, httpPost } from "@eres/rest";
import {
	BlockCodeTransformer,
	checkApiRequestResponseError,
	convertErrorCodesToMessage,
	EjariPropertiesFromApi,
	EjariPropertyFromApi,
	MapApiLookUpTransformer,
	processLeaseResponse,
	TenantDocumentTransformer,
	TermsTransformer,
	validateEjariProperty,
	ValidateSecondPhaseMgmContract
} from "./Utils";
import {
	ApiEjariProperty,
	ApiLookup,
	EjariProperty,
	EjariPropertyType,
	EjariResponse,
	LeaseResponse,
	Lookup,
	PropEditDataModel,
	RentalRangeProperty,
	RentalRangePropertyRequest,
	TenantDocumentLookup,
	TenantUserProfile,
	ValidatePropertyRequest,
	ValidatePropertyResponse
} from "./Entity";
import {
	Contract,
	ContractComputeRequest,
	ContractFinance,
	PayByTenantRequest,
	PayByTenantResponse,
	TerminateRequest,
	ValidateDewaMoveOut
} from "./Entity/contract";
import {
	AttachmentInfo,
	MayBeError,
	TenancyContract,
	TenancyContractResponse,
	TenancyRequest,
	Tenant,
	TenantContractInfo,
	TenantRegiModel,
	TenantTerminateRequest
} from "./Entity/tenant";
import { ProjectProperty, PropertiesWithProject } from "./Entity/LeasedProperties";
import { getEjariActiveUserId, getEjariManagementId, getEjariParticipantId, getEjariUserType, getEmail } from "@10x/sso";
import { ContractTypes, EjariLookUp } from "./constants";
import { GRID_SIZE } from "@my-dld/components/DataView/GridDataView";
import { CoOccupant, CoOccupantInfo, CoOccupantPropertyInfo, CoOccupantSearchInfo, PetInfo } from "./Entity/co_occupants";
import {
	GetManagementContractDetails,
	MCContractCreateRequest,
	MCRequest,
	MCTerminateRequest,
	ValidateMGMContractRequest
} from "./Entity/management_contract";
import {
	GetContractPropertiesWithProjectsResponse,
	GetMobilePropertyDetailsResponse,
	GetProjectPropertiesResponse,
	GetPropertiesWithProjectsResponse
} from "./EjariSecurityUtil";

function normaliseContract( contract: Contract ): Promise<Contract> {
	if ( contract && contract.Tenants ) {
		for ( let tenant of contract.Tenants ) {
			if ( tenant.Person && tenant.Person.EmiratesId != null ) {
				tenant.Person.EmiratesId = String( tenant.Person.EmiratesId );
			}
		}
	}
	return Promise.resolve( contract );
}

class EjariApi implements IEjariApi {
	getDownloadPDFurl( contractKey: string ): string {
		return cfg.ejariAPI().endPoint() + "api/publiccertificate/GetContractToStream?ContractNumber=" + contractKey;
	}

	getLookup( name: string | number, param1: string = "", param2: string = "", transformer: LookupTransformer = MapApiLookUpTransformer ): Promise<Lookup[]> {
		return httpGet<EjariResponse<ApiLookup[]>>( cfg.ejariAPI().endPoint() +
			`api/Lookups/GetData?name=${ name }&param1=${ param1 }&param2=${ param2 }` )
		.then( checkApiRequestResponseError )
		.then( apiLookups => {
			return apiLookups.map( transformer );
		} );
	}

	getPropertyUsages( propertyId: string | number, isFreeHold: boolean, propTypeId: number ): Promise<Lookup[]> {
		const lookupName = propTypeId === EjariPropertyType.VILLA ? "VillaPropertyUsages" : "UnitPropertyUsages";
		return this.getLookup( lookupName, propertyId + "", isFreeHold + "" );
	}

	getPropertyTypes( usageId: number, propTypeId: number ): Promise<Lookup[]> {
		return this.getLookup( "EjariPropertyTypes", usageId + "", propTypeId + "" );
	}

	getPropertySubTypes( typeId: number ): Promise<Lookup[]> {
		return this.getLookup( "EjariPropertySubTypes", typeId + "" );
	}

	updatePropertyDetail( property: PropEditDataModel ): Promise<{ EjariPropertyId: number }> {
		return httpPost<EjariResponse<{ EjariPropertyId: number }>>( cfg.ejariAPI().endPoint() + "api/publiccertificate/UpdatePropertyInformation", property )
		.then( checkApiRequestResponseError );
	}

	getContractDetails( contractNumber: string, userId: string ): Promise<Contract> {
		return httpGet<EjariResponse<Contract>>( `${ cfg.ejariAPI().endPoint() }api/PublicCertificate/GetContract?ContractNumber=${ contractNumber }&operationuserid=${ userId }` )
		.then( checkApiRequestResponseError )
		.then( normaliseContract );
	}

	terminateContract( req: TerminateRequest ): Promise<{ Identity: number }> {
		return httpPost<EjariResponse<{ Identity: number }>>( `${ cfg.ejariAPI().endPoint() }api/PublicCertificate/TerminateContract`, req )
		.then( checkApiRequestResponseError );
	}

	validateProperty( req: ValidatePropertyRequest, notAnErrorCodes: ( string | number ) [] = [] ): Promise<ValidatePropertyResponse> {
		return httpGet<EjariResponse<ValidatePropertyResponse>>( `${ cfg.ejariAPI().endPoint() }api/PublicCertificate/GetValidateProperty?${ toParams( req ) }` )
		.then( x => checkApiRequestResponseError( x, notAnErrorCodes ) );
	}

	searchPersonTenant( req: PersonTenantSearchRequest ): Promise<Tenant> {
		const normalisedRequest: PersonTenantSearchRequest = {
			NationalityCountryId: "",
			MobileNumber: "",
			EmiratesId: "",
			PassportNo: "",
			TenantNumber: "",
			DOB: "",
			...req as Partial<PersonTenantSearchRequest>,
		};
		return httpGet<EjariResponse<Tenant & MayBeError>>( `${ cfg.ejariAPI().endPoint() }api/PublicCertificate/GetTenantPerson?${ toParams( normalisedRequest ) }` )
		.then( x => checkApiRequestResponseError( x, [ "0" ], true ) )
		.then( x => {
			// only throw dob not matched validation error
			if ( x && x.ContractValidationErrors && Array.isArray( x.ContractValidationErrors ) && x.ContractValidationErrors.length > 0 ) {
				const dobError = x.ContractValidationErrors.find( ex => ex.ErrorEnum === "DOBNotMatched" );
				if ( dobError ) {
					throw dobError;
				}
			}
			return x;
		} );
	}

	searchCompanyTenant( req: CompanyTenantSearch ): Promise<Tenant> {
		const normalisedRequest: CompanyTenantSearch = {
			companyName: "",
			Issuer: "",
			LicenseStateId: "",
			TenantNumber: "",
			TradeLicenseNo: "",
			...req,
		};
		return httpGet<EjariResponse<Tenant>>( `${ cfg.ejariAPI().endPoint() }api/PublicCertificate/GetTenantCompany?${ toParams( normalisedRequest ) }` )
		.then( x => checkApiRequestResponseError( x, [ "0" ], true ) );
	}

	getTenantCompany( req: CompanyTenantSearch ): Promise<Tenant> {
		const normalisedRequest: CompanyTenantSearch = {
			companyName: "",
			Issuer: "",
			LicenseStateId: "",
			TenantNumber: "",
			TradeLicenseNo: "",
			Email: getEmail(),
			...req,
		};

		return httpPost( cfg.ejariAPI().endPoint() + "api/PublicCertificate/GetTenantCompany", normalisedRequest )
		.then( x => checkApiRequestResponseError( x, [ "0" ], false ) )
		.then( ( resp: any ) => {
			return resp;
		} );
	}

	verifyOTP(req: VerifyOTP): Promise<Tenant> {
		return httpPost( cfg.ejariAPI().endPoint() + "api/publiccertificate/ValidateOtp", req )
		.then( x => checkApiRequestResponseError( x, [ "0" ], false ) )
		.then( ( resp: any ) => {
			return resp;
		} );
	}

	validateTenant( tenant: Tenant, userId: number ): Promise<void> {
		return httpPost<EjariResponse<number>>( `${ cfg.ejariAPI().endPoint() }api/PublicCertificate/ValidateTenant?operatingUserID=${ userId }`, tenant )
		.then( x => checkApiRequestResponseError( x, [ "0" ], true ) )
		.then( x => {
			if ( x === 0 ) return Promise.resolve();
			else return new Promise( async ( resolve, reject ) => {
				let msg = await convertErrorCodesToMessage( [ x ], [] );
				reject( msg );
			} );
		});
	}

	calculateContractValue( req: ContractComputeRequest ): Promise<ContractFinance> {
		return httpPost<EjariResponse<ContractFinance>>( `${ cfg.ejariAPI().endPoint() }api/PublicCertificate/DoCalculations`, req )
		.then( checkApiRequestResponseError );
	}

	submitContract( contract: Contract ): Promise<Contract> {
		return httpPost<EjariResponse<Contract>>( cfg.ejariAPI().endPoint() + "api/PublicCertificate/SubmitContract", contract )
		.then( checkApiRequestResponseError );
	}

	renewContract( contract: Contract ): Promise<Contract> {
		return httpPost<EjariResponse<Contract>>( cfg.ejariAPI().endPoint() + "api/PublicCertificate/RenewContract", contract )
		.then( checkApiRequestResponseError );
	}

	requestPayByTenant( request: PayByTenantRequest ) {
		return httpGet<EjariResponse<PayByTenantResponse>>( cfg.ejariAPI().endPoint() + "api/publiccertificate/SendActivationLink?" +
			`ContractNumber=${ request.contractNumber }&Mobile=${ request.mobile }&Email=${ request.email }` )
		.then( checkApiRequestResponseError );
	}

	getEjariPaymentShortUrl( contract: Contract ): string {
		return cfg.ejariAPI().shortUrl() + contract.ContractNumber;
	}

	getEjariPaymentUrl( contract: Contract ) {
		return cfg.ejariAPI().endPoint() + cfg.ejariAPI().paymentPage() + `?code=${ contract.ContractNumber }&emb=true`;
	}

	getPropertyDetail( propertyIdOrKey: string, isFreeHold: boolean, propTypeID: number ): Promise<EjariProperty> {
		return httpGet<EjariResponse<ApiEjariProperty>>( cfg.ejariPropertyWalletAPI().endPoint() +
			`property/GetMobilePropertyDetails?IsFreeHold=${ isFreeHold }&PropertyTypeId=${ propTypeID }&PropertyId=${ propertyIdOrKey }&rowValue=` )
		.then(( resp: any ) => {
			return EjariPropertyFromApi( GetMobilePropertyDetailsResponse( resp ).Response );
		});
	}

	getPropertyDetailsSecure(searchQuery: string) {
		return httpGet( cfg.ejariPropertyWalletAPI().endPoint() + "property/GetMobilePropertyDetails?PropertyTypeId=&IsFreeHold=&PropertyId=&rowValue=" + searchQuery )
		.then( ( resp: any ) => {
			return EjariPropertyFromApi( GetMobilePropertyDetailsResponse( resp ).Response );
		} );
	}

	getMyLeases( mobile: string, eid: string ): Promise<LeaseResponse> {
		return httpGet( cfg.ejariPropertyWalletAPI().endPoint() + "property/GetContractPropertiesWithProjects?" +
			"pageIndex=0&pageSize=500" )
		.then( r => {
			return processLeaseResponse( GetContractPropertiesWithProjectsResponse(r) );
		} );
	}

	getPropertiesWithProjects( searchQuery: string ): Promise<PropertiesWithProject[]> {
		return httpGet( cfg.ejariPropertyWalletAPI().endPoint() + "property/GetPropertiesWithProjects?" + searchQuery )
		.then( ( resp: any ) => {
			return resp ? GetPropertiesWithProjectsResponse(resp) : [];
		} );
	}

	getProjectProperties( searchQuery: string ): Promise<ProjectProperty[]> {
		return httpGet( cfg.ejariPropertyWalletAPI().endPoint() + "property/GetProjectProperties?" + searchQuery )
		.then( ( resp: any ) => {
			return resp ? GetProjectPropertiesResponse(resp) : [];
		} );
	}

	getBuildingUnits( searchQuery: string ): Promise<ProjectProperty[]> {
		return httpGet( cfg.ejariPropertyWalletAPI().endPoint() + "property/GetBuildingUnits?" + searchQuery )
		.then( ( resp: any ) => {
			return resp ? GetProjectPropertiesResponse(resp) : [];
		} );
	}

	getProjectVillaList( searchQuery: string ): Promise<any> {
		return httpGet( cfg.ejariPropertyWalletAPI().endPoint() + "property/GetLandVillas?" + searchQuery )
		.then( ( resp: any ) => {
			return resp ? GetProjectPropertiesResponse(resp) : [];
		} );
	}

	searchTenantProperty( request: TenantPropertySearch ): Promise<EjariProperty[]> {
		return httpPost<EjariResponse<ApiEjariProperty[]>>( cfg.ejariAPI().endPoint() + "api/PublicIntegration/SearchProperties", request )
		.then( checkApiRequestResponseError )
		.then( EjariPropertiesFromApi );
	}

	validateTenantProperty( ejariPropertyID: string, propertyID: number, propertyTypeID: number, isFreehold: boolean,
	                        notAnErrorCodes: ( string | number ) [] = [] ): Promise<{ property: EjariProperty, AllowedCoTenants: number } | null> {
		return httpGet( cfg.ejariAPI().endPoint() + "api/certificate/ValidateProperty?activeUserTypeId=12&" +
			`ejariPropertyId=${ ejariPropertyID }&propertyTypeId=${ propertyTypeID }&propertyId=${ propertyID }&propertyFreeholdId=${ isFreehold ? 1 : 0 }` )
		.then( resp => checkApiRequestResponseError( resp, notAnErrorCodes ) ).then( ( resp: any ) => {
			return resp ? { property: resp.PropertiesList[ 0 ], AllowedCoTenants: resp.AllowedCoTenants } : null;
		} );
	}

	verifyTenantUserProfile( request: VerifyTentUserProfile ): Promise<TenantUserProfile> {
		return httpPost( cfg.ejariAPI().endPoint() + "api/PublicIntegration/VerifyUserProfile", request )
		.then( checkApiRequestResponseError ).then( ( resp: any ) => {
			return resp;
		} );
	}

	getTenantContractDocuments( reqTypeId: number ): Promise<TenantDocumentLookup[]> {
		return httpGet<EjariResponse<ApiLookup[]>>( `${ cfg.ejariAPI().endPoint() }api/lookups/GetAttachments?requestTypeId=${ reqTypeId }` )
		.then( checkApiRequestResponseError ).then( documentLookups => {
			return documentLookups.map( TenantDocumentTransformer );
		} );
	}

	getTenantTerms(): Promise<Lookup[]> {
		return this.getLookup( EjariLookUp.TERMS_CONDITIONS, "", "", TermsTransformer );
	}

	getTenantRequestDetail( request: TenantReqDetailsRequest ): Promise<TenantContractInfo> {
		return httpPost<EjariResponse<TenantContractInfo>>( cfg.ejariAPI().endPoint() +
			"api/PublicCertificate/GetTenancyRequestDetails?", request ).then( checkApiRequestResponseError ).then( ( resp: any ) => {
			return resp.Contract;
		} );
	}

	getTemporaryContractId(): Promise<{ TempContractId: number }> {
		return httpGet<EjariResponse<{ TempContractId: number }>>( cfg.ejariAPI().endPoint() +
			"api/ejaricontract/generatetemporaryid" ).then( checkApiRequestResponseError );
	}

	uploadTenancyContractDocument( base64data: any, type: string, docId: number, contractId: number, documentName: string ): Promise<AttachmentInfo> {
		let data = base64ToArrayBuffer( base64data );
		return httpPost<EjariResponse<AttachmentInfo>>( cfg.ejariAPI().endPoint() + "api/documents/UploadAttachment?" +
			`ActivityDocumentId=${ docId }&ContractId=${ contractId }&DocumentName=${ documentName }.${ type }`, data,
			{ "Content-Type": "application/octet-stream" } ).then( checkApiRequestResponseError );
	}

	deleteTenancyContractDocument( DocumentPageId: number ): Promise<{ IsDocumentDeleted: boolean }> {
		return httpPost<EjariResponse<{ IsDocumentDeleted: boolean }>>( cfg.ejariAPI().endPoint() + "api/documents/DeleteAttachment",
			{ DocumentPageId: DocumentPageId } ).then( checkApiRequestResponseError );
	}

	submitTenancyContract( contract: TenancyContract ): Promise<TenancyContractResponse> {
		return httpPost<EjariResponse<TenancyContractResponse>>( cfg.ejariAPI().endPoint() + "api/PublicCertificate/SubmitContract", contract )
		.then( checkApiRequestResponseError );
	}

	renewTenancyContract( contract: TenancyContract ): Promise<TenancyContractResponse> {
		return httpPost<EjariResponse<TenancyContractResponse>>( cfg.ejariAPI().endPoint() + "api/PublicCertificate/RenewContract", contract )
		.then( checkApiRequestResponseError );
	}

	terminateTenancyContract( req: TenantTerminateRequest ): Promise<TenancyContractResponse> {
		return httpPost<EjariResponse<TenancyContractResponse>>( cfg.ejariAPI().endPoint() + "api/PublicCertificate/TerminateContract", req )
		.then( checkApiRequestResponseError );
	}

	updateTenancyContract( contract: TenancyContract ): Promise<TenantContractInfo> {
		return httpPost<EjariResponse<TenantContractInfo>>( cfg.ejariAPI().endPoint() + "api/PublicCertificate/UpdateTenancyRequest", contract )
		.then( checkApiRequestResponseError );
	}

	updateTerminationTenancyContract( req: TenantTerminateRequest ): Promise<TenantContractInfo> {
		return httpPost<EjariResponse<TenantContractInfo>>( cfg.ejariAPI().endPoint() + "api/PublicCertificate/UpdateTerminationTenancyRequest", req )
		.then( checkApiRequestResponseError );
	}

	getTenancyRequests( ownerId: number, tenantId: number, userTypeId: number, pageNumber: number = 1, pageSize: number = 500 ): Promise<TenancyRequest[]> {
		return httpGet<EjariResponse<TenancyRequest[]>>( cfg.ejariAPI().endPoint() + "api/tenant/GetTenancyRequests?" +
			`contractStatusId=0&createionStartDate=&creationEndDate=&managementCompanyId=0&ownerId=${ ownerId }` +
			`&tenantId=${ tenantId }&userTypeId=${ userTypeId }&pageNumber=${ pageNumber }&pageSize=${ pageSize }` )
		.then( checkApiRequestResponseError ).then( ( resp: any ) => resp.TenancyResponse );
	}

	registerTenant( request: TenantRegiModel ): Promise<TenantUserProfile> {
		return httpPost<EjariResponse<TenantUserProfile>>( cfg.ejariAPI().endPoint() + "api/PublicIntegration/CreateUserProfile", request )
		.then( checkApiRequestResponseError );
	}

	loadRentalRangeByProperty( request: RentalRangePropertyRequest ): Promise<RentalRangeProperty> {
		return httpGet<EjariResponse<RentalRangeProperty>>( cfg.ejariRxApi().endPoint() + "PropertyRentalIndexData?language=EN" +
			"&PropertyId=" + request.propertyId + "&PropertyTypeId=" + request.propertyTypeId + "&IsFreeHold=" + request.isFreeHold +
			"&ContractEndDate=" + request.contractEndDate + "&ContractAmount=" + ( request.contractAmount ?? "0" ) )
		.then( checkApiRequestResponseError );
	}

	//co occupants
	getCoOccupants( contractId: number, contractType: number, propertyTypeId?: number, isFreeHold?: boolean ): Promise<CoOccupantInfo> {
		return httpPost<EjariResponse<CoOccupantInfo>>( cfg.ejariAPI().endPoint()
			+ "api/PublicCertificate/GetTenancyRequestCoocupants",
			{
				ContractType: contractType,
				ContractId: contractId,
				PropertyTypeId: propertyTypeId,
				IsFreeHold: isFreeHold
			} )
		.then( x => checkApiRequestResponseError( x, [ 6 ] ) );
	}

	searchCoOccupants( searchInfo: CoOccupantSearchInfo ): Promise<CoOccupant> {
		return httpGet<EjariResponse<CoOccupant>>( cfg.ejariAPI().endPoint() + `api/publiccertificate/GetCoTenantPerson?${ toParams( searchInfo ) }` )
		.then( checkApiRequestResponseError );
	}

	updateCoOccupants( contract: CoOccupantPropertyInfo, coOccupants: CoOccupant[], count: number ): Promise<CoOccupantInfo> {
		return httpPost<EjariResponse<CoOccupantInfo>>( cfg.ejariAPI().endPoint() + "api/PublicCertificate/SubmitCoocupant",
			{ ...contract, CoTenants: coOccupants, TotalCoTenants: count } )
		.then( x => checkApiRequestResponseError( x, [ 6 ] ) );

	}

	getPets( contractId: number, contractType: number ): Promise<PetInfo[]> {
		return httpPost<EjariResponse<{ Request: { CoTenantPets?: PetInfo[] } }>>( cfg.ejariAPI().endPoint()
			+ "api/PublicCertificate/GetTenancyRequestCoocupantPets",
			{ ContractType: contractType, ContractId: contractId } )
		.then( x => checkApiRequestResponseError( x ) )
		.then( x => x?.Request?.CoTenantPets ?? [] )
		.then( r => r.filter( x => x.Count > 0 ) );
	}

	updatePets( contract: CoOccupantPropertyInfo, pets: PetInfo[] ): Promise<PetInfo[]> {
		return httpPost<EjariResponse<{ Request: { CoTenantPets?: PetInfo[] } }>>( cfg.ejariAPI().endPoint() + "api/PublicCertificate/SubmitCoocupantPets",
			{ ...contract, CoTenantPets: pets, } )
		.then( x => checkApiRequestResponseError( x, [ 6 ] ) )
		.then( x => x?.Request?.CoTenantPets ?? [] )
		.then( r => r.filter( x => x.Count > 0 ) );
	}

	validateDewaMoveOut( moveOutRequest: ValidateDewaMoveOut ): Promise<any> {
		return httpPost<EjariResponse<any>>( cfg.ejariAPI().endPoint() + "api/PublicCertificate/ValidateDewaMoveOut",
			moveOutRequest )
		.then( x => checkApiRequestResponseError( x, [ 1 ] ) );
	}

	validateManagementContract( request: MCContractCreateRequest ) {
		return httpPost( cfg.ejariAPI().endPoint() + "api/ManagementContract/ValidateManagementContractRequestByOwner", request )
		.then( x => checkApiRequestResponseError( x, [ 3, 5 ] ) );
	}

	createManagementContract( request: MCContractCreateRequest ) {
		return httpPost<EjariResponse<any>>( cfg.ejariAPI().endPoint() + "api/ManagementContract/CreateManagementContractByOwner", request )
		.then( x => checkApiRequestResponseError( x, [ 3, 5 ], false, ValidateSecondPhaseMgmContract ) );
	}

	renewManagementContract( request: MCContractCreateRequest ) {
		return httpPost<EjariResponse<any>>( cfg.ejariAPI().endPoint() + "api/ManagementContract/RenewManagementContractByOwner", request )
		.then( x => checkApiRequestResponseError( x, [ 3, 5 ], false, ValidateSecondPhaseMgmContract ) );
	}

	getManagementContract(): Promise<{ TerminationRequests: MCRequest[] }> {
		const request = {
			ActiveUserID: getEjariActiveUserId(),
			ActiveUserType: getEjariUserType(),
			OwnerParticipantID: getEjariParticipantId(),
		};
		return httpPost( cfg.ejariAPI().endPoint() + "api/ManagementContract/GetMyTerminationRequests", request )
		.then( x => checkApiRequestResponseError( x, [ 3, 5 ] ) );
	}

	getManagementContractDetails( managementContractId: number ): Promise<GetManagementContractDetails> {
		const request = {
			ActiveUserID: getEjariActiveUserId(),
			ActiveUserType: getEjariUserType(),
			OwnerParticipantID: getEjariParticipantId(),
			ManagementContractId: managementContractId,
		};
		return httpPost<EjariResponse<GetManagementContractDetails>>( cfg.ejariAPI().endPoint() + "api/ManagementContract/GetManagementContractDetails", request )
		.then( checkApiRequestResponseError );
	}

	terminateManagementContract( request: MCTerminateRequest ) {
		return httpPost( cfg.ejariAPI().endPoint() + "api/ManagementContract/TerminateManagementContractByOwner", request )
		.then( x => checkApiRequestResponseError( x, [ 3, 5 ] ) );
	}

	getPredefinedTermsTemplates( participantID: number ) {
		return httpGet( cfg.ejariAPI().endPoint() + `api/PublicCertificate/GetPredefinedTermsTemplates?operatingUserParentID=${ participantID }` );
	}

	getPredefinedTerm( participantID: number, templateId: number, contractType?: ContractTypes ) {
		let type = contractType ?? ContractTypes.TENANCY;
		return httpGet( cfg.ejariAPI().endPoint() + `api/PublicCertificate/GetPredefinedTerms?operatingUserParentID=${ participantID }&templateID=${ templateId }&contracttype=${ type }` );
	}

	validatePropertiesForMGMContract( request: ValidateMGMContractRequest, skipPhaseTwo = true ): Promise<any> {
		return httpPost( cfg.ejariAPI().endPoint() + "api/ManagementContract/ValidateManagementContractProperties", request )
		.then( x => checkApiRequestResponseError( x, undefined, skipPhaseTwo ) );
	}
}

export const PropertyCoOccupantContractType = 1001;

export function toParams( data: object ): string {
	const keys = Object.keys( data );
	const kvs: string[] = [];
	for ( const key of keys ) {
		let v = ( data as any )[ key ];
		if ( v == null ) {
			kvs.push( key + "=" );
		} else {
			v = encodeURIComponent( v.toString() );
			kvs.push( key + "=" + v );
		}

	}
	return kvs.join( "&" );
}

const api = new EjariApi();
export default api;
const BlockReasons: Lookup[] = [];

export async function getErrorLookups(): Promise<Lookup[]> {
	if ( BlockReasons && BlockReasons.length > 0 )
		return BlockReasons;
	const reasons = await api.getLookup( "EjariContractBlockReasons", "", "", BlockCodeTransformer );
	if ( reasons ) {
		BlockReasons.push( ...reasons );
	}
	return BlockReasons;
}


export function getProjectsSearchQuery( data?: LeasedPropertySearch ) {
	let query = getLeaseSearchQuery( data );
	query += "&rentalStatus=" + ( ( data && data.PropertyStatus ) ? data.PropertyStatus : 0 );
	query += "&startIndex=0";
	query += "&maxRows=0";
	return query;
}

export function getPropertiesRowSearchQuery( startIndex: number, rentalStatus: number| string, rowValue?: string) {
	let query = "";
	query += "rowValue=" + rowValue;
	query += "&rentalStatus=" + (rentalStatus ? rentalStatus : 0);
	query += "&pageIndex=" + startIndex;
	query += "&pageSize=50";
	return query;
}

export function getPropertiesSearchQuery( projectId: number, startIndex: number, rentalStatus: string, data?: LeasedPropertySearch ) {
	let query = getLeaseSearchQuery( data );
	query += "&projectId=" + projectId;
	query += "&rentalStatus=" + ( rentalStatus ? rentalStatus : 0 );
	query += "&startIndex=" + startIndex;
	query += "&maxRows=" + GRID_SIZE;
	return query;
}

export function getUnitsSearchQuery( buildingPropertyIdOrKey: number | string, startIndex: number, rentalStatus: string, data?: LeasedPropertySearch ) {
	let query = getLeaseSearchQuery( data );
	query += "&buildingPropertyId=" + buildingPropertyIdOrKey;
	query += "&rentalStatus=" + ( rentalStatus ? rentalStatus : 0 );
	query += "&startIndex=" + startIndex;
	query += "&maxRows=" + GRID_SIZE;
	return query;
}

export function getAllUnitsSearchQuery( buildingPropertyIdOrKey?: number | string ) {
	let query = getLeaseSearchQuery();
	query += "&buildingPropertyId=" + buildingPropertyIdOrKey;
	query += "&rentalStatus=0";
	query += "&startIndex=0";
	query += "&maxRows=0";
	return query;
}

export function getLeaseSearchQuery( data?: LeasedPropertySearch ) {
	const userTypeId = getEjariUserType();
	let query = "contractAmountFrom=" + ( ( data && data.RentalAmountFrom ) ? data.RentalAmountFrom : "" );
	query += "&propertyNumber=" + ( ( data && data.UnitNumber ) ? data.UnitNumber : "" );
	query += "&userTypeId=" + userTypeId;
	query += "&contractEndFrom=" + ( ( data && data.ContractStartDate ) ? data.ContractStartDate : "" );
	query += "&logitude=";
	query += "&managementCompanyId=" + getEjariManagementId();
	query += "&viewMode=0";
	query += "&buildingName=" + ( ( data && data.BuildingName ) ? data.BuildingName : "" );
	query += "&radius=";
	query += "&dmSubNo=" + ( ( data && data.DMSubNumber ) ? data.DMSubNumber : "" );
	query += "&areaId=" + ( ( data && data.Area ) ? data.Area : "" );
	query += "&dewaPremise=" + ( ( data && data.DewaNumber ) ? data.DewaNumber : "" );
	query += "&contractEndTo=" + ( ( data && data.ContractEndDate ) ? data.ContractEndDate : "" );
	query += "&participantid=" + getEjariParticipantId();
	query += "&contractAmountTo=" + ( ( data && data.RentalAmountTo ) ? data.RentalAmountTo : "" );
	query += "&latitude=";
	query += "&dmNo=" + ( ( data && data.DMNumber ) ? data.DMNumber : "" );
	query += "&propertyTypeId=" + ( ( data && data.PropertyType ) ? data.PropertyType : "" );
	query += "&dummyParam=";
	query += "&contracNumber=" + ( ( data && data.ContractNumber ) ? data.ContractNumber : "" );
	return query;
}
